import React, { JSX, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import API from "../api/axiosInstance";
import { Table, TableField, TenantFlagRule } from "../types/interfaces";
import { createUUID } from "../utils/validators";
import Icon from "@mui/material/Icon";

// Import our flag icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import StarIcon from "@mui/icons-material/Star";
import FlagIcon from "@mui/icons-material/Flag";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloudIcon from "@mui/icons-material/Cloud";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventIcon from "@mui/icons-material/Event";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import HelpIcon from "@mui/icons-material/Help";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import BuildIcon from "@mui/icons-material/Build";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ChatIcon from "@mui/icons-material/Chat";
import CodeIcon from "@mui/icons-material/Code";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GroupIcon from "@mui/icons-material/Group";
import HistoryIcon from "@mui/icons-material/History";
import LabelIcon from "@mui/icons-material/Label";
import LanguageIcon from "@mui/icons-material/Language";
import ListIcon from "@mui/icons-material/List";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WorkIcon from "@mui/icons-material/Work";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CallIcon from "@mui/icons-material/Call";
import DirectionsIcon from "@mui/icons-material/Directions";
import EmailIcon from "@mui/icons-material/Email";
import ExtensionIcon from "@mui/icons-material/Extension";
import FaceIcon from "@mui/icons-material/Face";
import FilterListIcon from "@mui/icons-material/FilterList";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import HeadsetIcon from "@mui/icons-material/Headset";
import InvertColorsIcon from "@mui/icons-material/InvertColors";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import MapIcon from "@mui/icons-material/Map";
import NavigationIcon from "@mui/icons-material/Navigation";
import PaletteIcon from "@mui/icons-material/Palette";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import RadioIcon from "@mui/icons-material/Radio";
import SchoolIcon from "@mui/icons-material/School";
import TerrainIcon from "@mui/icons-material/Terrain";
import UsbIcon from "@mui/icons-material/Usb";
import VideocamIcon from "@mui/icons-material/Videocam";
import WatchIcon from "@mui/icons-material/Watch";
import WifiIcon from "@mui/icons-material/Wifi";

export const iconMap: Record<string, JSX.Element> = {
    "check_circle": <CheckCircleIcon fontSize="small" />,
    "error": <ErrorIcon fontSize="small" />,
    "warning": <WarningIcon fontSize="small" />,
    "info": <InfoIcon fontSize="small" />,
    "star": <StarIcon fontSize="small" />,
    "flag": <FlagIcon fontSize="small" />,
    "lock": <LockIcon fontSize="small" />,
    "visibility": <VisibilityIcon fontSize="small" />,
    "edit": <EditIcon fontSize="small" />,
    "delete": <DeleteIcon fontSize="small" />,
    "add": <AddIcon fontSize="small" />,
    "remove": <RemoveIcon fontSize="small" />,
    "cloud": <CloudIcon fontSize="small" />,
    "download": <DownloadIcon fontSize="small" />,
    "upload": <UploadIcon fontSize="small" />,
    "schedule": <ScheduleIcon fontSize="small" />,
    "event": <EventIcon fontSize="small" />,
    "home": <HomeIcon fontSize="small" />,
    "settings": <SettingsIcon fontSize="small" />,
    "search": <SearchIcon fontSize="small" />,
    "account_circle": <AccountCircleIcon fontSize="small" />,
    "shopping_cart": <ShoppingCartIcon fontSize="small" />,
    "thumb_up": <ThumbUpIcon fontSize="small" />,
    "thumb_down": <ThumbDownIcon fontSize="small" />,
    "help": <HelpIcon fontSize="small" />,
    "announcement": <AnnouncementIcon fontSize="small" />,
    "build": <BuildIcon fontSize="small" />,
    "camera_alt": <CameraAltIcon fontSize="small" />,
    "chat": <ChatIcon fontSize="small" />,
    "code": <CodeIcon fontSize="small" />,
    "dashboard": <DashboardIcon fontSize="small" />,
    "favorite": <FavoriteIcon fontSize="small" />,
    "group": <GroupIcon fontSize="small" />,
    "history": <HistoryIcon fontSize="small" />,
    "label": <LabelIcon fontSize="small" />,
    "language": <LanguageIcon fontSize="small" />,
    "list": <ListIcon fontSize="small" />,
    "location_on": <LocationOnIcon fontSize="small" />,
    "menu": <MenuIcon fontSize="small" />,
    "notifications": <NotificationsIcon fontSize="small" />,
    "person": <PersonIcon fontSize="small" />,
    "phone": <PhoneIcon fontSize="small" />,
    "print": <PrintIcon fontSize="small" />,
    "save": <SaveIcon fontSize="small" />,
    "send": <SendIcon fontSize="small" />,
    "share": <ShareIcon fontSize="small" />,
    "visibility_off": <VisibilityOffIcon fontSize="small" />,
    "work": <WorkIcon fontSize="small" />,
    "zoom_in": <ZoomInIcon fontSize="small" />,
    "zoom_out": <ZoomOutIcon fontSize="small" />,
    "attach_file": <AttachFileIcon fontSize="small" />,
    "battery_full": <BatteryFullIcon fontSize="small" />,
    "bookmark": <BookmarkIcon fontSize="small" />,
    "brightness_4": <Brightness4Icon fontSize="small" />,
    "calendar_today": <CalendarTodayIcon fontSize="small" />,
    "call": <CallIcon fontSize="small" />,
    "directions": <DirectionsIcon fontSize="small" />,
    "email": <EmailIcon fontSize="small" />,
    "extension": <ExtensionIcon fontSize="small" />,
    "face": <FaceIcon fontSize="small" />,
    "filter_list": <FilterListIcon fontSize="small" />,
    "gps_fixed": <GpsFixedIcon fontSize="small" />,
    "headset": <HeadsetIcon fontSize="small" />,
    "invert_colors": <InvertColorsIcon fontSize="small" />,
    "keyboard": <KeyboardIcon fontSize="small" />,
    "lightbulb": <LightbulbIcon fontSize="small" />,
    "map": <MapIcon fontSize="small" />,
    "navigation": <NavigationIcon fontSize="small" />,
    "palette": <PaletteIcon fontSize="small" />,
    "queue_music": <QueueMusicIcon fontSize="small" />,
    "radio": <RadioIcon fontSize="small" />,
    "school": <SchoolIcon fontSize="small" />,
    "terrain": <TerrainIcon fontSize="small" />,
    "usb": <UsbIcon fontSize="small" />,
    "videocam": <VideocamIcon fontSize="small" />,
    "watch": <WatchIcon fontSize="small" />,
    "wifi": <WifiIcon fontSize="small" />
  };
  

interface RulesPanelProps {
  table: Table;
  tenantId: string;
}

const operatorOptions = [
  { value: "==", label: "=" },
  { value: "!=", label: "≠" },
  { value: ">", label: ">" },
  { value: "<", label: "<" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Does Not Contain" },
  { value: "is_blank", label: "Is Blank" },
];

const iconOptions = [
  "check_circle",
  "error",
  "warning",
  "info",
  "star",
  "flag",
  "lock",
  "visibility",
  "edit",
  "delete",
  "add",
  "remove",
  "cloud",
  "download",
  "upload",
  "schedule",
  "event",
  "home",
  "settings",
  "search",
  "account_circle",
  "shopping_cart",
  "thumb_up",
  "thumb_down",
  "help",
  "announcement",
  "build",
  "camera_alt",
  "chat",
  "code",
  "dashboard",
  "favorite",
  "group",
  "history",
  "label",
  "language",
  "list",
  "location_on",
  "menu",
  "notifications",
  "person",
  "phone",
  "print",
  "save",
  "send",
  "share",
  "visibility_off",
  "work",
  "zoom_in",
  "zoom_out",
  "attach_file",
  "battery_full",
  "bookmark",
  "brightness_4",
  "calendar_today",
  "call",
  "directions",
  "email",
  "extension",
  "face",
  "filter_list",
  "gps_fixed",
  "headset",
  "invert_colors",
  "keyboard",
  "lightbulb",
  "map",
  "navigation",
  "palette",
  "queue_music",
  "radio",
  "school",
  "terrain",
  "usb",
  "videocam",
  "watch",
  "wifi",
];

const RulesPanel: React.FC<RulesPanelProps> = ({ table, tenantId }) => {
  const [rules, setRules] = useState<TenantFlagRule[]>([]);
  const [fields, setFields] = useState<TableField[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<TenantFlagRule | null>(null);

  useEffect(() => {
    fetchRules();
    fetchFields();
  }, []);

  const fetchRules = async () => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
    
      const res = await API.get(`/tables/flags/${user.user.tenantID}`,headers);
      setRules(res.data);
    } catch (error) {
      console.error("Failed to fetch rules:", error);
    }
  };

  const fetchFields = async () => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
    
      const res = await API.get(`/tables/${table.tableID}/fields`,headers);
      setFields(res.data);
    } catch (error) {
      console.error("Failed to fetch fields:", error);
    }
  };

  const handleOpenDialog = (rule?: TenantFlagRule) => {
    if (rule) {
      setEditingRule({ ...rule });
    } else {
      setEditingRule({
        id: createUUID(),
        tenantId,
        ruleOrder: rules.length + 1,
        ruleName: "",
        fieldName: "",
        comparisonOperator: "==",
        comparisonValue: "",
        matchCreatedByMe: false,
        matchCreatedBefore: null,
        rowColor: "",
        iconName: "",
        tooltipText: "",
        isEnabled: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setEditingRule(null);
    setOpenDialog(false);
  };

  const handleSaveRule = async () => {
    if (!editingRule) return;
    const user = JSON.parse(localStorage.getItem('user') || "{}");
    const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };

    try {
      if (rules.some((r) => r.id === editingRule.id)) {
        await API.put("/tables/flags", editingRule,headers);
      } else {
        await API.post("/tables/flags", editingRule,headers);
      }
      fetchRules();
      handleCloseDialog();
    } catch (error) {
      console.error("Failed to save rule:", error);
    }
  };

  const handleDeleteRule = async (id: string) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
    
      await API.delete(`/tables/flags/${id}`,headers);
      fetchRules();
    } catch (error) {
      console.error("Failed to delete rule:", error);
    }
  };

  const columns: ColDef[] = [
    { field: "ruleOrder", headerName: "Order", width: 80 },
    { field: "ruleName", headerName: "Rule Name", flex: 1 },
    { field: "fieldName", headerName: "Field", flex: 1 },
    {
      field: "comparisonOperator",
      headerName: "Operator",
      flex: 1,
      valueFormatter: (params) => {
        const op = operatorOptions.find((o) => o.value === params.value);
        return op ? op.label : params.value;
      },
    },
    { field: "comparisonValue", headerName: "Value", flex: 1 },
    {
      field: "rowColor",
      headerName: "Row Color",
      flex: 1,
      cellRenderer: (params: any) => (
        <Box
          sx={{
            width: 20,
            height: 20,
            backgroundColor: params.value || "transparent",
            border: "1px solid #ccc",
          }}
        />
      ),
    },
    {
        field: "iconName",
        headerName: "Icon",
        flex: 1,
        cellRenderer: (params: any) =>
            params.value && iconMap[params.value] ? (
            <Box display="flex" alignItems="center">
                {iconMap[params.value]}
                <Typography variant="body2" ml={1}>{params.value}</Typography>
            </Box>
            ) : null,
        },
    { field: "tooltipText", headerName: "Tooltip", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      cellRenderer: (params: any) => (
        <Box>
          <Button
            size="small"
            onClick={() => handleOpenDialog(params.data)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => handleDeleteRule(params.data.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Row Flag Rules for: <strong>{table.tableName}</strong>
      </Typography>
      <Button variant="contained" onClick={() => handleOpenDialog()}>
        + Add Rule
      </Button>
      <Box
        className="ag-theme-alpine"
        sx={{ height: 400, width: "100%", mt: 2 }}
      >
        <AgGridReact rowData={rules} columnDefs={columns} />
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRule?.ruleName ? "Edit Rule" : "Add Rule"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Rule Name"
                fullWidth
                value={editingRule?.ruleName || ""}
                onChange={(e) =>
                  setEditingRule((prev) =>
                    prev ? { ...prev, ruleName: e.target.value } : prev
                  )
                }
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Field Name</InputLabel>
                <Select
                  value={editingRule?.fieldName || ""}
                  onChange={(e) =>
                    setEditingRule((prev) =>
                      prev ? { ...prev, fieldName: e.target.value } : prev
                    )
                  }
                  label="Field Name"
                >
                  {fields.map((field) => (
                    <MenuItem key={field.fieldID} value={field.fieldName}>
                      {field.fieldName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={editingRule?.comparisonOperator || ""}
                  onChange={(e) =>
                    setEditingRule((prev) =>
                      prev
                        ? { ...prev, comparisonOperator: e.target.value }
                        : prev
                    )
                  }
                  label="Operator"
                  >
                  {operatorOptions.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Comparison Value"
                fullWidth
                value={editingRule?.comparisonValue || ""}
                onChange={(e) =>
                  setEditingRule((prev) =>
                    prev ? { ...prev, comparisonValue: e.target.value } : prev
                  )
                }
              />
            </Grid>

            <Grid item xs={12}>
  <Typography variant="subtitle2" gutterBottom>Row Color</Typography>
  <Box display="flex" alignItems="center" gap={2}>
    <Select
      value={editingRule?.rowColor || ""}
      onChange={(e) =>
        setEditingRule((prev) =>
          prev ? { ...prev, rowColor: e.target.value } : prev
        )
      }
      size="small"
      displayEmpty
      sx={{ minWidth: 160 }}
    >
      <MenuItem value="">None</MenuItem>
      {[
        "#F44336", "#E91E63", "#9C27B0", "#673AB7", "#3F51B5", "#2196F3",
        "#03A9F4", "#00BCD4", "#009688", "#4CAF50", "#8BC34A", "#CDDC39",
        "#FFEB3B", "#FFC107", "#FF9800", "#FF5722", "#795548", "#9E9E9E",
        "#607D8B", "#000000", "#ffffff", "#B0BEC5", "#CFD8DC", "#263238"
      ].map((color) => (
        <MenuItem key={color} value={color}>
          <Box
            sx={{
              display: "inline-block",
              width: 16,
              height: 16,
              backgroundColor: color,
              borderRadius: "50%",
              mr: 1,
              border: '1px solid #ccc',
            }}
          />
          {color}
        </MenuItem>
      ))}
    </Select>

    <input
      type="color"
      aria-label="Custom color picker"
      value={editingRule?.rowColor || "#ffffff"}
      onChange={(e) =>
        setEditingRule((prev) =>
          prev ? { ...prev, rowColor: e.target.value } : prev
        )
      }
      style={{
        width: 40,
        height: 40,
        border: "none",
        background: "none",
        cursor: "pointer"
      }}
    />
  </Box>
</Grid>

            <Grid item xs={12}>
            <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                value={editingRule?.iconName || ""}
                onChange={(e) =>
                    setEditingRule((prev) =>
                    prev ? { ...prev, iconName: e.target.value } : prev
                    )
                }
                label="Icon"
                renderValue={(value) =>
                    value && iconMap[value] ? (
                    <Box display="flex" alignItems="center">
                        {iconMap[value]}
                        <Typography variant="body2" ml={1}>{value}</Typography>
                    </Box>
                    ) : (
                    ""
                    )
                }
                >
                {iconOptions.map((icon) => (
                    <MenuItem key={icon} value={icon}>
                    <Box display="flex" alignItems="center">
                        {iconMap[icon]}
                        <Typography variant="body2" ml={1}>{icon}</Typography>
                    </Box>
                    </MenuItem>
                ))}
                </Select>
            </FormControl>
            </Grid>


            <Grid item xs={12}>
              <TextField
                label="Tooltip Text"
                fullWidth
                value={editingRule?.tooltipText || ""}
                onChange={(e) =>
                  setEditingRule((prev) =>
                    prev ? { ...prev, tooltipText: e.target.value } : prev
                  )
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRule}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RulesPanel;
